/**
 * Office job-page purchase-order card (Issue #181): the job's window into
 * procurement. A repair job created from an estimate auto-drafts a parts PO
 * (createDraftPOFromEstimate) stamped with job_id, but until now nothing on
 * the job read it back — an in-flight PO lived only on the /purchasing board.
 * This surfaces each PO from draft onward, with its status chip and a link
 * that opens it on the board.
 *
 * When the job has no PO yet but DOES have a source estimate, it offers a
 * one-tap "Create PO from the quote" (the parts-ordering entry point office
 * staff kept asking for — it was previously reachable only from the
 * Purchasing board).
 *
 * Custom PO (2026-11-02): the office can ALWAYS start a custom PO for an
 * open job — with or without an estimate, and whether or not the estimate has
 * anything orderable. When "from the quote" finds nothing it can order
 * (NO_ORDERABLE_LINES — e.g. the part is priced as labor on the quote), the
 * card says so and offers the custom PO instead of a dead-end error. A
 * cancelled job offers neither.
 *
 * A cancelled PO doesn't count as "has a PO": the card keeps showing it, but
 * still offers Create PO from the quote underneath, so a PO cancelled to change
 * vendor can be re-drafted from the job (the server already allows it).
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingCart, ArrowRight, Plus, PencilLine } from "lucide-react";
import { PoQuoteUpdate } from "./po-quote-update";
import { NOTHING_TO_ORDER_WORDS, shouldOfferCustomPo, useStartCustomPo } from "@/pages/purchasing/custom-po";

interface JobPo {
    id: string;
    po_number: string | null;
    status: string;
    total_amount: string | number | null;
    vendor_name: string | null;
    line_count: number;
    expected_delivery_date: string | null;
    confirmed_delivery_date: string | null;
    created_at: string;
    /** #1629 — set when "Update PO from quote" replaced this (cancelled) PO. */
    superseded_by_po_number?: string | null;
}

// Same palette as the Purchasing board (index.tsx statusColors) so a PO reads
// the same on the job as it does on the board.
const STATUS_COLORS: Record<string, string> = {
    draft: "bg-muted text-muted-foreground",
    needs_review: "bg-destructive/10 text-destructive",
    rfq_sent: "bg-sky-500/15 text-sky-700 dark:text-sky-300",
    quoted: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300",
    variance_review: "bg-red-500/15 text-red-700 dark:text-red-300",
    ready_to_finalize: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    final_review: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    final_sent: "bg-warning/10 text-warning",
    confirmed: "bg-teal-500/15 text-teal-700 dark:text-teal-300",
    receiving: "bg-teal-500/15 text-teal-700 dark:text-teal-300",
    closed: "bg-success/10 text-success",
    cancelled: "bg-muted text-muted-foreground",
};

const STATUS_LABELS: Record<string, string> = {
    draft: "Draft",
    needs_review: "Needs review",
    rfq_sent: "RFQ sent",
    quoted: "Quoted",
    variance_review: "Variance review",
    ready_to_finalize: "Ready to finalize",
    final_review: "Final review",
    final_sent: "Final sent",
    confirmed: "Confirmed",
    receiving: "Receiving",
    closed: "Closed",
    cancelled: "Cancelled",
};

// Dates arrive as YYYY-MM-DD (a DATE column). Split rather than new Date() so
// a UTC-midnight parse can't shift the day in a negative-offset timezone.
function shortDate(iso: string | null): string | null {
    if (!iso) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return null;
    return `${Number(m[2])}/${Number(m[3])}`;
}

export function JobPoCard({
    jobId,
    sourceEstimateId,
    jobStatus,
}: {
    jobId: string;
    sourceEstimateId?: string | null;
    jobStatus?: string | null;
}) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    // Set when "from the quote" found nothing it could order: the card then
    // leads with the custom PO instead of the quote button.
    const [nothingToOrder, setNothingToOrder] = useState(false);
    const startCustom = useStartCustomPo((poId) => navigate(`/purchasing?po=${poId}`));
    const { data } = useQuery({
        queryKey: ["job-purchase-orders", jobId],
        queryFn: () => api.get<{ orders: JobPo[] }>(`/api/purchase-orders/for-job/${jobId}`),
        staleTime: 30_000,
    });

    // Draft a PO from the job's source estimate (parts with a vendor SKU are
    // added automatically). Idempotent on the server: it returns the existing
    // live PO (409 PO_EXISTS) instead of duplicating.
    const createPo = useMutation({
        mutationFn: () =>
            api.post<{ poId?: string | null; poNumber?: string | null }>(`/api/purchasing/jobs/${jobId}/create-po`, {}),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["job-purchase-orders", jobId] });
            qc.invalidateQueries({ queryKey: ["purchasing"] });
            if (r.poId) {
                toast.success(
                    r.poNumber
                        ? `Draft PO ${r.poNumber} created — opening Purchasing.`
                        : "Draft PO created — opening Purchasing.",
                );
                navigate(`/purchasing?po=${r.poId}`);
            }
        },
        onError: (e: any) => {
            const err = e?.body?.error;
            if (err === "PO_EXISTS" && e?.body?.poId) {
                toast.info("A purchase order already exists for this job — opening it.");
                qc.invalidateQueries({ queryKey: ["job-purchase-orders", jobId] });
                navigate(`/purchasing?po=${e.body.poId}`);
            } else if (shouldOfferCustomPo(e)) {
                // Not a dead end: the card swaps to "Start a custom PO".
                setNothingToOrder(true);
            } else {
                toast.error(`Couldn't create the PO: ${e?.body?.message ?? err ?? e?.message ?? "unknown"}`);
            }
        },
    });

    const orders = data?.orders ?? [];
    const hasLivePo = orders.some((po) => po.status !== "cancelled");
    const jobCancelled = jobStatus === "cancelled";
    const canCreate = !hasLivePo && !!sourceEstimateId && !nothingToOrder && !jobCancelled;
    const canCustom = !jobCancelled;
    const busy = createPo.isPending || startCustom.isPending;

    const createButton = (
        <button
            type="button"
            onClick={() => createPo.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
        >
            <Plus className="h-4 w-4" /> {createPo.isPending ? "Creating…" : "Create PO from the quote"}
        </button>
    );

    // Primary when it is the only way forward; quiet next to the quote button.
    const customButton = (primary: boolean) => (
        <button
            type="button"
            onClick={() => startCustom.mutate(jobId)}
            disabled={busy}
            data-testid="start-custom-po"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition disabled:opacity-60 ${
                primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border hover:bg-accent"
            }`}
        >
            <PencilLine className="h-4 w-4" /> {startCustom.isPending ? "Starting…" : "Start a custom PO"}
        </button>
    );

    // What to offer when the job has no live PO.
    const startSection = (lead: string | null) => {
        if (canCreate) {
            return (
                <>
                    <p className="mb-2 text-xs text-muted-foreground">
                        {lead ? `${lead} ` : ""}Make one from the quote (parts with a supplier number go on by
                        themselves), or start a custom PO and type in what you need.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        {createButton}
                        {canCustom && customButton(false)}
                    </div>
                </>
            );
        }
        if (!canCustom) return null;
        return (
            <>
                <p className="mb-2 text-xs text-muted-foreground" data-testid="custom-po-lead">
                    {nothingToOrder
                        ? NOTHING_TO_ORDER_WORDS
                        : `${lead ? `${lead} ` : ""}Start a custom PO and type in what you need.`}
                </p>
                {customButton(true)}
            </>
        );
    };

    if (orders.length === 0) {
        const section = startSection("No purchase order yet.");
        if (!section) return null;
        return (
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                        <ShoppingCart className="h-4 w-4" /> Purchase order
                    </CardTitle>
                </CardHeader>
                <CardContent>{section}</CardContent>
            </Card>
        );
    }

    const afterCancelled = hasLivePo ? null : startSection("Every PO on this job is cancelled.");
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm">
                    <ShoppingCart className="h-4 w-4" /> Purchase order{orders.length === 1 ? "" : "s"}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {/* #1629 — the estimate changed after the PO was built: one
                    strip, one button. Renders nothing when they match. */}
                {hasLivePo && sourceEstimateId && <PoQuoteUpdate jobId={jobId} />}
                {orders.map((po) => {
                    const delivery = shortDate(po.confirmed_delivery_date) ?? shortDate(po.expected_delivery_date);
                    const deliveryLabel = po.confirmed_delivery_date ? "arrives" : "eta";
                    const total = po.total_amount != null ? Number(po.total_amount) : 0;
                    return (
                        <Link
                            key={po.id}
                            to={`/purchasing?po=${po.id}`}
                            className="flex items-center justify-between gap-2 rounded-md border p-2 transition hover:bg-accent/50"
                        >
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                                    <span className="font-mono">{po.po_number ?? "PO (draft)"}</span>
                                    <span
                                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                            STATUS_COLORS[po.status] ?? "bg-muted text-muted-foreground"
                                        }`}
                                    >
                                        {STATUS_LABELS[po.status] ?? po.status}
                                    </span>
                                    {po.superseded_by_po_number && (
                                        <span className="text-xs text-muted-foreground">
                                            replaced by {po.superseded_by_po_number}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
                                    {po.vendor_name && <span>{po.vendor_name}</span>}
                                    <span>
                                        {po.line_count} line{po.line_count === 1 ? "" : "s"}
                                    </span>
                                    {total > 0 && <span>${Math.round(total)}</span>}
                                    {delivery && (
                                        <span>
                                            {deliveryLabel} {delivery}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        </Link>
                    );
                })}
                {!hasLivePo
                    ? afterCancelled && <div className="pt-1">{afterCancelled}</div>
                    : canCustom && (
                          // Something not on the PO? Another custom PO, any time.
                          <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-muted-foreground">
                              <span>Need something else?</span>
                              {customButton(false)}
                          </div>
                      )}
            </CardContent>
        </Card>
    );
}
